import React, { useEffect, useState } from 'react';
import { TrendingUp, Check, DollarSign, Trophy } from 'lucide-react';

import { Spinner } from 'design-system/react';
import logoLight from 'design-system/src/assets/lean-agility-logo.svg';
import logoDark from 'design-system/src/assets/lean-agility-logo-dark.svg';

import { moneyFormatter } from '../../utils';
import { useSimulationResults } from '../../hooks';

import { Page } from '../organisms';

export function SimulationResultPage() {
  const { data, isLoading, isError, error } = useSimulationResults();

  return (
    <Page title={null} hideBackButton>
      <div className="flex flex-col w-full items-center">
        {isLoading && <LoadingResults />}
        {isError && <LoadingError error={error} />}
        {data && <SimulationResultsData data={data} />}
      </div>
    </Page>
  );
}

function LoadingResults() {
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-2xl font-bold text-center">Loading simulation results...</p>
      <Spinner size="xl" />
    </div>
  );
}

interface LoadingErrorProps {
  error: Error | null;
}

function LoadingError({ error }: LoadingErrorProps) {
  return (
    <div className="flex flex-col items-center">
      <div className="mb-16">
        <p className="text-2xl font-bold text-center">Unable to load simulation results, sorry :(</p>
      </div>
      <div className="mt-16">
        <p className="text-xs text-text-secondary">{error?.message}</p>
      </div>
    </div>
  );
}

interface SimulationResultsData {
  tasks_completed: number;
  features_completed: number;
  earned: number;
  earnings_per_second: number;
  team_name: string;
}

interface SimulationResultsDataProps {
  data: SimulationResultsData;
}

function SimulationResultsData({ data }: SimulationResultsDataProps) {
  const delay = (order: number) => order * 1000;

  return (
    <div className="rounded-xl border border-border-default bg-bg-elevated shadow-sm overflow-hidden">
      <div className="p-6 pb-4">
        <AnimatedItem delay={200}>
          <h2 className="text-xl font-bold text-center">How well did you do?</h2>
        </AnimatedItem>
      </div>

      <div className="p-6 pt-2 flex flex-col gap-6">
        <AnimatedStatItem
          icon={<Check className="w-5 h-5 text-action-primary" />}
          label="Tasks completed in your team"
          delay={delay(1)}
        >
          {data.tasks_completed}
        </AnimatedStatItem>

        <AnimatedStatItem
          icon={<Trophy className="w-5 h-5 text-feedback-warning" />}
          label="Features completed by the organization"
          delay={delay(2)}
        >
          {data.features_completed}
        </AnimatedStatItem>
        <AnimatedStatItem
          icon={<DollarSign className="w-5 h-5 text-feedback-success" />}
          label="Money earned by the organization"
          delay={delay(3)}
        >
          {moneyFormatter.format(data.earned)}
        </AnimatedStatItem>
        <AnimatedStatItem
          icon={<TrendingUp className="w-5 h-5 text-primary-500" />}
          label="Final earnings rate per second"
          delay={delay(4)}
        >
          {moneyFormatter.format(data.earnings_per_second)}
        </AnimatedStatItem>
      </div>

      <AnimatedItem delay={delay(5)}>
        <div className="flex flex-col items-center gap-1 py-4 border-t border-border-default">
          <img src={logoLight} alt="Lean Agility" className="w-28 dark:hidden" />
          <img src={logoDark} alt="Lean Agility" className="w-28 hidden dark:block" />
        </div>
      </AnimatedItem>
    </div>
  );
}

interface AnimatedItemProps {
  delay: number;
  children: React.ReactNode;
}

function AnimatedItem({ delay, children }: AnimatedItemProps) {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className="transition-all duration-500 ease-out"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'scale(1)' : 'scale(0.8)',
      }}
    >
      {children}
    </div>
  );
}

interface StatItemProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}

function StatItem({ icon, label, children }: StatItemProps) {
  return (
    <div className="flex items-center">
      <div className="mr-3">{icon}</div>
      <div className="flex-1">
        <p className="text-sm font-medium text-text-secondary">{label}</p>
        <p className="text-xl font-bold">{children}</p>
      </div>
    </div>
  );
}

interface AnimatedStatItemProps {
  icon: React.ReactNode;
  label: string;
  delay: number;
  children: React.ReactNode;
}

function AnimatedStatItem({ icon, label, delay, children }: AnimatedStatItemProps) {
  return (
    <AnimatedItem delay={delay}>
      <StatItem icon={icon} label={label}>
        {children}
      </StatItem>
    </AnimatedItem>
  );
}
